import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import type { ImageTransforms } from '../Asset'

const ImageTransformDefaultsContext = createContext<ImageTransforms>({})

type ImageTransformDefaultsProviderProps = Readonly<{
  children: ReactNode
  transforms: ImageTransforms
}>

/**
 * Supply the transforms `ImageProxy` uses for images whose caller asks for no
 * particular size.
 *
 * Asked for no dimensions, the asset proxy serves the original upload — which
 * for print-resolution photography is tens of megabytes, and which the browser
 * then has to decode in full. On screen that is merely wasteful. Offscreen
 * renderers pay for it directly: the snapshot browser aborts a page that has
 * not settled within its navigation timeout, so a single oversized asset can
 * stop a preview from ever being generated.
 *
 * Wrap a tree whose images are never displayed above a known size, and every
 * uncapped image below it asks the proxy for that size instead. Transforms
 * passed to an individual `ImageProxy` still win.
 */
export const ImageTransformDefaultsProvider = ({
  children,
  transforms,
}: ImageTransformDefaultsProviderProps): JSX.Element => (
  <ImageTransformDefaultsContext.Provider value={transforms}>
    {children}
  </ImageTransformDefaultsContext.Provider>
)

export const useImageTransformDefaults = (): ImageTransforms =>
  useContext(ImageTransformDefaultsContext)
